import { useEffect, useMemo, useState } from 'react';

import { DEBOUNCE_TIME } from '@/constants/general';
import AutocompleteField from '@/form/AutocompleteField';
import {
  useGetWorkerRelationshipsByCustomerLazyQuery,
  useListWorkersForAutocompleteLazyQuery,
} from '@/graphql';
import useAuth from '@/hooks/useAuth';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import { WorkerItem } from '@/routes/Agency/Job/WorkerAutocompleteField';
import {
  CompareOperatorEnum,
  RelationshipKindEnum,
  WorkerFilterSetInput,
} from '@/types/graphql';

// Adapted from src/routes/Agency/Job/WorkerAutocompleteField for the pool
// add-worker flow: that field also hides FAVORITED workers (it feeds the
// favorites-add modal), which is wrong here — favorited workers are prime
// pool candidates. Only BLOCKED workers are excluded (P14); the backend
// re-checks the block on submit either way.

type Props = {
  customerId: string;
  fieldId: string;
  label: string;
  skillId?: string;
};

const PoolWorkerAutocompleteField = ({
  customerId,
  fieldId,
  label,
  skillId,
}: Props) => {
  const { currentAgency } = useAuth();
  const [fetch, { data: listWorkersData }] =
    useListWorkersForAutocompleteLazyQuery({ fetchPolicy: 'no-cache' });
  const [getWorkerRelationship, { data: workerRelationshipsData }] =
    useGetWorkerRelationshipsByCustomerLazyQuery();

  const items = useMemo(() => {
    if (!listWorkersData) return [];

    const blockedIds = new Set(
      (workerRelationshipsData?.customer.workerRelationships ?? [])
        .filter((item) => item.kind === RelationshipKindEnum.BLOCKED)
        .map(({ worker }) => worker.id),
    );

    return listWorkersData.agency.workers.items.filter(
      (worker) => !blockedIds.has(worker.id),
    );
  }, [listWorkersData, workerRelationshipsData]);

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_TIME);

  useEffect(() => {
    let filters: WorkerFilterSetInput =
      debouncedQuery.length > 0 ? { query: { value: debouncedQuery } } : {};

    if (skillId) {
      filters = {
        ...filters,
        workerSkills: [{ op: CompareOperatorEnum.EQ, value: Number(skillId) }],
      };
    }

    getWorkerRelationship({ variables: { customerId, filters: null } });
    fetch({
      variables: {
        agencyId: currentAgency!.id,
        filters,
      },
    });
  }, [customerId, debouncedQuery]);

  return (
    <AutocompleteField<WorkerItem>
      autoFocus
      required
      disabled={false}
      fieldId={fieldId}
      itemToKey={(item) => item.id}
      itemToString={(item) => {
        if (!item) return '';
        if (item.user.middleName) {
          return `${item.user.firstName} ${item.user.middleName} ${item.user.lastName}`;
        }
        return `${item.user.firstName} ${item.user.lastName}`;
      }}
      items={items}
      label={label}
      openMenuOnInputFocus
      placeholder="Select GravyWorker..."
      onInputValueChange={({ inputValue }) => setQuery(inputValue ?? '')}
    />
  );
};

export default PoolWorkerAutocompleteField;
